import { useMemo, useCallback } from "react"
import { useGet } from "../utils/apiComponent/useApi"
import { endpoints } from "../utils/endpoints"

/**
 * Hook to fetch lessons for a specific module
 * @param {string} moduleId - The ID of the module to fetch lessons for
 * @returns {Object} - { lessons, loading, error, refetch }
 */
export function useLessons(moduleId) {
  // Memoize the endpoint to prevent unnecessary re-renders
  const endpoint = useMemo(() => {
    if (!moduleId) return null
    return `${endpoints.moduleLessons}?moduleId=${moduleId}`
  }, [moduleId])
  
  const shouldFetch = Boolean(moduleId)
  
  const {
    data: lessonsData,
    loading,
    error,
    execute: executeFetch
  } = useGet(
    shouldFetch && endpoint ? endpoint : `${endpoints.moduleLessons}?moduleId=disabled`,
    {
      immediate: shouldFetch
    }
  )

  const refetch = useCallback(() => executeFetch(undefined, { force: true }), [executeFetch])

  // Memoize the return object to prevent unnecessary re-renders
  const filteredLessons = useMemo(() => {
    if (!shouldFetch || !lessonsData?.lessons) return []
    // Filtrer par moduleId pour s'assurer qu'on n'affiche que les leçons du bon module
    return lessonsData.lessons.filter(lesson => {
      const lessonModuleId = lesson.moduleId || lesson.module?.id
      return lessonModuleId === moduleId
    })
  }, [shouldFetch, lessonsData, moduleId])

  return useMemo(() => ({
    lessons: filteredLessons,
    loading: shouldFetch ? loading : false,
    error: shouldFetch ? error : null,
    refetch: shouldFetch ? refetch : () => {},
    total: shouldFetch ? (lessonsData?.total || filteredLessons.length) : 0,
    page: shouldFetch ? (lessonsData?.page || 1) : 1,
    totalPages: shouldFetch ? (lessonsData?.totalPages || 1) : 1
  }), [shouldFetch, filteredLessons, loading, error, refetch, lessonsData])
}

/**
 * Hook to fetch all lessons (without module filter)
 * @returns {Object} - { lessons, loading, error, refetch }
 */
export function useAllLessons() {
  const {
    data: lessonsData,
    loading,
    error,
    execute: refetch
  } = useGet(endpoints.moduleLessons)

  return {
    lessons: lessonsData?.lessons || [],
    loading,
    error,
    refetch,
    total: lessonsData?.total || 0,
    page: lessonsData?.page || 1,
    totalPages: lessonsData?.totalPages || 1
  }
}

